import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { submitLead } from '../lib/submitLead';

const LeadMagnetModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [downloadTriggered, setDownloadTriggered] = useState(false);

  // Trigger download when submission succeeds
  useEffect(() => {
    if (submitSuccess && !downloadTriggered) {
      setDownloadTriggered(true);
      
      // Trigger download
      const pdfUrl = '/marketing-for-dummies.pdf';
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'marketing-for-dummies.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close after 15 seconds
      setTimeout(() => onClose(), 15000);
    }
  }, [submitSuccess, downloadTriggered, onClose]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({ firstName: '', lastName: '', email: '' });
      setSubmitSuccess(false);
      setError(null);
      setDownloadTriggered(false);
    }
  }, [isOpen]);

  // Body scroll control
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Send the lead to Formspree (lands in your email inbox)
      await submitLead({
        _source: 'Marketing for Dummies download',
        _subject: 'New PDF download lead',
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
      });

      // Success!
      setSubmitSuccess(true);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <ModalWrapper>
            <ModalContainer
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
            >
              <CloseButton onClick={onClose}>✕</CloseButton>
              
              {submitSuccess ? (
                <SuccessContent>
                  <SuccessEmoji>🎉</SuccessEmoji>
                  <SuccessTitle>Your Download is Starting!</SuccessTitle>
                  <SuccessText>
                    <strong>Marketing for Dummies</strong> should download automatically in a moment.
                    <br /><br />
                    Check your Downloads folder or browser's download bar.
                    <br /><br />
                    <em>If the download doesn't start automatically, click the button below.</em>
                  </SuccessText>
                  
                  <ManualDownloadButton onClick={() => {
                    const pdfUrl = '/marketing-for-dummies.pdf';
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = 'marketing-for-dummies.pdf';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}>
                    📥 Click Here to Download
                  </ManualDownloadButton>
                  
                  <CloseNote>This window closes automatically in 15 seconds...</CloseNote>
                  
                  <ProgressBar>
                    <ProgressFill
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 15 }}
                    />
                  </ProgressBar>
                </SuccessContent>
              ) : (
                <>
                  <ModalHeader>
                    <CharacterSmall>
                      <SmallFace>
                        <SmallEyes>
                          <SmallEye />
                          <SmallEye />
                        </SmallEyes>
                        <SmallSmile />
                      </SmallFace>
                    </CharacterSmall>
                    
                    <ModalTitle>
                      Get Your Free <ColorText>Marketing Guide!</ColorText>
                    </ModalTitle>
                    
                    <ModalSubtitle>
                      Your download starts automatically after you submit!
                    </ModalSubtitle>
                  </ModalHeader>

                  <Form onSubmit={handleSubmit}>
                    <InputRow>
                      <Input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                      <Input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </InputRow>
                    
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />

                    {error && (
                      <ErrorMessage>
                        {error}
                      </ErrorMessage>
                    )}

                    <Benefits>
                      <Benefit>✓ 20 Fun Illustrated Chapters</Benefit>
                      <Benefit>✓ Instant PDF Download</Benefit>
                      <Benefit>✓ Beginner Friendly Guide</Benefit>
                    </Benefits>
                    
                    <SubmitButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? '⏳ SENDING...' : '🎁 GET MY FREE PDF NOW!'}
                    </SubmitButton>
                    
                    <Privacy>🔒 No spam. Unsubscribe anytime.</Privacy>
                  </Form>
                </>
              )}
            </ModalContainer>
          </ModalWrapper>
        </>
      )}
    </AnimatePresence>
  );
};

// Styled Components (keeping all the same styles)
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 9998;
`;

const ModalWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  padding-top: max(1rem, env(safe-area-inset-top));
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const ModalContainer = styled(motion.div)`
  background: linear-gradient(135deg, #FFF5E6 0%, #FFE5F1 100%);
  border: 3px solid #333;
  border-radius: 25px;
  padding: 2.5rem 2rem;
  max-width: 480px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 2rem);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: white;
  border: 2px solid #333;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  font-size: 1.25rem;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: #FFD93D;
    transform: rotate(90deg);
  }
  
  &:active {
    transform: rotate(90deg) scale(0.95);
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const CharacterSmall = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FFD93D 0%, #FFB347 100%);
  border-radius: 50%;
  margin: 0 auto 1rem;
  position: relative;
  border: 2px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SmallFace = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SmallEyes = styled.div`
  display: flex;
  gap: 20px;
`;

const SmallEye = styled.div`
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 5px;
    background: #333;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const SmallSmile = styled.div`
  width: 25px;
  height: 12px;
  border: 2px solid #333;
  border-top: none;
  border-radius: 0 0 25px 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 900;
  margin-bottom: 0.5rem;
  color: #333;
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ColorText = styled.span`
  color: #FF6B6B;
`;

const ModalSubtitle = styled.p`
  color: #666;
  font-size: 1rem;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #333;
  border-radius: 12px;
  color: #333;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4ECDC4;
    transform: scale(1.02);
  }
  
  &::placeholder {
    color: #999;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.875rem;
    font-size: 0.95rem;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: #FFE5E5;
  border: 2px solid #FF6B6B;
  border-radius: 12px;
  color: #D32F2F;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
`;

const Benefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: white;
  border: 2px dashed #4ECDC4;
  border-radius: 12px;
`;

const Benefit = styled.div`
  color: #333;
  font-size: 0.9rem;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 100%);
  color: white;
  border: 2px solid #333;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 800;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
`;

const Privacy = styled.p`
  font-size: 0.875rem;
  color: #666;
  text-align: center;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const SuccessContent = styled.div`
  text-align: center;
  padding: 2rem 0;
  
  @media (max-width: 480px) {
    padding: 1.5rem 0;
  }
`;

const SuccessEmoji = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: bounce 0.6s ease;
  
  @keyframes bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.2); }
  }
  
  @media (max-width: 480px) {
    font-size: 3rem;
  }
`;

const SuccessTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 900;
  color: #333;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const SuccessText = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  font-weight: 600;
  line-height: 1.6;
  font-size: 0.95rem;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ManualDownloadButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #4ECDC4 0%, #44A3D3 100%);
  color: white;
  border: 2px solid #333;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    background: linear-gradient(135deg, #44A3D3 0%, #4ECDC4 100%);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    padding: 0.65rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const CloseNote = styled.p`
  font-size: 0.85rem;
  color: #999;
  margin: 1rem 0;
  font-style: italic;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #E0E0E0;
  border-radius: 999px;
  overflow: hidden;
  max-width: 300px;
  margin: 0 auto;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #FF6B6B, #4ECDC4, #FFD93D);
`;

export default LeadMagnetModal;